/* eslint-disable */
import { useMemo, useState } from 'react';
import { ScanLine, Hash } from 'lucide-react';
// eslint-disable-next-line import/no-cycle
import { ListTable } from '../../ListTable';
import { BaseModal } from '../BaseModal';
import { Input } from '@/Components/ui/input';

/**
 * Read-only view of one asset audit's per-asset-type report.
 *
 * Deliberately a copy of AuditItemsModal's shape — same BaseModal size and the
 * same legend / search / ListTable layout and spacing (1rem gaps) — so the
 * three audit tabs read alike. Only the columns differ, because an
 * asset audit counts asset types rather than SKUs.
 *
 * Everything shown already arrives on the /list payload as the audit's
 * `reports`, so opening this costs no extra call.
 */

const Legend = ({ items }) => {
	return (
		<div className='grid grid-cols-2 gap-1'>
			{items.map(({ label, subText, color }) => (
				<div
					key={label}
					className='flex items-start gap-2 bg-gray-50 shadow-sm rounded-lg p-2 min-h-[60px]'
				>
					<div
						className='w-3 h-3 mt-1 rounded-full flex-shrink-0'
						style={{
							backgroundColor: color,
						}}
					/>
					<div className='flex flex-col'>
						<p className='text-sm font-semibold text-gray-800'>{label}</p>
						{subText && (
							<p className='text-xs text-gray-500 leading-snug'>{subText}</p>
						)}
					</div>
				</div>
			))}
		</div>
	);
};

// One figure in the totals strip. Amber/red match the legend dots, so a
// non-zero unexpected/missing total reads the same way a tinted row does.
const TotalFigure = ({ label, value, color }) => {
	return (
		<div className='flex flex-col leading-tight'>
			<span className='text-lg font-semibold' style={color ? { color } : {}}>
				{value}
			</span>
			<span className='text-xs text-gray-500'>{label}</span>
		</div>
	);
};

const LEGENDS_DATA = [
	{
		label: 'Unexpected',
		subText:
			'Assets found at this store that the ledger placed somewhere else. Counted here, and moved to this store when the audit is completed',
		color: '#F59E0B', // amber-500
	},
	{
		label: 'Missing',
		subText:
			'Assets the ledger expected at this store that were not found. Written off from this store when the audit is completed',
		color: '#EF4444', // red-500
	},
];

const COLMAP = {
	'Asset Type': 'assetTypeName',
	'Counted By': 'id',
	'Expected (A)': 'expectedQty',
	'Found (B)': 'foundQty',
	'Unexpected (C)': 'unexpectedQty',
	'Missing (D)': 'missingQty',
};

const COLUMNS = [
	{
		title: 'Asset Type',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Counted By',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Expected (A)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Found (B)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Unexpected (C)',
		config: {
			type: 'DATA',
		},
	},
	{
		title: 'Missing (D)',
		config: {
			type: 'DATA',
		},
	},
];

const AssetAuditReportModal = ({ audit, handleClose }) => {
	const [searchQuery, setSearchQuery] = useState('');

	const reports = audit?.reports ?? [];

	const DATA = useMemo(() => {
		const term = searchQuery.trim().toLowerCase();
		return reports.filter((report) =>
			term
				? (report?.assetTypeName ?? '').toLowerCase().includes(term)
				: true
		);
	}, [reports, searchQuery]);

	const totals = useMemo(
		() =>
			reports.reduce(
				(acc, report) => ({
					expected: acc.expected + (report?.expectedQty ?? 0),
					found: acc.found + (report?.foundQty ?? 0),
					unexpected: acc.unexpected + (report?.unexpectedQty ?? 0),
					missing: acc.missing + (report?.missingQty ?? 0),
				}),
				{ expected: 0, found: 0, unexpected: 0, missing: 0 }
			),
		[reports]
	);

	// Same convention as AuditItemsModal's getRowColor: the row itself carries
	// the signal, so a problem is visible without reading every number.
	const getRowColor = (report) => {
		if (report?.missingQty > 0) return { backgroundColor: '#FEE2E2' };
		if (report?.unexpectedQty > 0) return { backgroundColor: '#FEF3C7' };
		return {};
	};

	// EVERY column needs an entry here. Columns passed through `actions` are
	// rendered ONLY by special[title](id, val) — ListTable has no fallback to
	// the colMap value on that path (ListTable.js:519), so a column without a
	// renderer comes out blank. AuditItemsModal defines one per column for the
	// same reason.
	const SPECIAL_ITEMS = {
		'Asset Type': (id, val) => {
			return <div>{val ?? '-'}</div>;
		},
		'Counted By': (id) => {
			const report = reports?.find((ele) => ele?.id === id);
			const Icon = report?.traceable ? ScanLine : Hash;
			return (
				<span className='inline-flex items-center gap-1 text-[10px] px-2 py-[2px] rounded-full whitespace-nowrap bg-slate-100 text-slate-700'>
					<Icon className='size-3' />
					{report?.traceable ? 'Scanned (QR)' : 'Counted (Non-QR)'}
				</span>
			);
		},
		'Expected (A)': (id, val) => {
			return <div>{val ?? 0}</div>;
		},
		'Found (B)': (id, val) => {
			return <div>{val ?? 0}</div>;
		},
		'Unexpected (C)': (id, val) => {
			return <div>{val ?? 0}</div>;
		},
		'Missing (D)': (id, val) => {
			return <div>{val ?? 0}</div>;
		},
	};

	return (
		<BaseModal
			heading={
				audit?.name
					? `${audit.name} — asset audit items (id: ${audit?.id ?? '-'})`
					: `Asset audit items (id: ${audit?.id ?? '-'})`
			}
			open={Boolean(audit)}
			handleClose={handleClose}
			customStyles={{
				width: '95%',
				height: '90vh',
			}}
			hideFooter
		>
			<div className='flex flex-col h-full min-h-0 gap-4 overflow-hidden'>
				<Legend items={LEGENDS_DATA} />
				<p className='text-xs text-gray-500'>
					Found (B) already includes Unexpected (C) — it is everything counted
					at the store, whatever the ledger said.
				</p>
				<div className='flex flex-row flex-wrap items-center gap-6 px-3 py-2 rounded-lg bg-gray-50'>
					<TotalFigure label='Expected (A)' value={totals.expected} />
					<TotalFigure label='Found (B)' value={totals.found} />
					<TotalFigure
						label='Unexpected (C)'
						value={totals.unexpected}
						color='#B45309'
					/>
					<TotalFigure
						label='Missing (D)'
						value={totals.missing}
						color='#B91C1C'
					/>
				</div>
				<div className='flex flex-row items-end w-full gap-4'>
					<Input
						className='w-[16rem] focus:outline-none'
						value={searchQuery}
						onChange={(e) => {
							setSearchQuery(e.target.value);
						}}
						placeholder='Search Asset Type'
					/>
				</div>
				{reports.length === 0 ? (
					<div className='p-4 text-sm text-center text-gray-500 border border-dashed rounded-md'>
						This audit has no asset types recorded.
					</div>
				) : DATA.length === 0 ? (
					<div className='p-4 text-sm text-center text-gray-500 border border-dashed rounded-md'>
						No asset type matches “{searchQuery}”.
					</div>
				) : (
					<div className='flex-1 min-h-0 overflow-auto'>
						<ListTable
							colMap={COLMAP}
							columns={[]}
							data={DATA?.map((ele) => {
								return {
									...ele,
									rowStyles: getRowColor(ele),
								};
							})}
							actions={COLUMNS}
							special={SPECIAL_ITEMS}
							fixedHeaderTop='0'
						/>
					</div>
				)}
			</div>
		</BaseModal>
	);
};

export default AssetAuditReportModal;
